import _ from "lodash";
import React, { useRef, useEffect } from "react";
import classes from "./JumpToModal.scss";
import I18nHOC from "UIComponents/I18nHOC";
import ShortcutTooltip from "UIComponents/ShortcutTooltip";
import { withRouter } from "react-router";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { useKey } from "react-use";
import { getConfigurationQueryEnhancer } from "ChildrenPage/modules/ChildrenPageEnhancers";
import {
  getConfigurationQueryVariable,
  globalSearchConfigurationSelector,
} from "AppComponents/GlobalSearchBarV2/modules/GlobalSearchBarModule";
import {
  updateJumpToCurriculumContext,
  setActiveFeatureWithKeyboardShortcuts,
} from "modules/Services";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  IconButton,
  Modal,
  Tabs,
  useKeyboardShortcuts,
  useKeyboardShortcutsContext,
  Dropdown,
  ButtonDisclosure,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import { recordEvent } from "lib/eventTracker";

import { navigateToFeature } from "Courses/components/TeacherCourses/module/Module";
import {
  getFeatureFromKeyboardEvent,
  JUMP_TO_COLUMNS,
  trackJumpToCardClick,
} from "GlobalHeader/modules/Utils";
import ConditionalTooltip from "BehaviourManagement/commonComponents/ConditionalTooltip";

import {
  goToCourseRoute,
  goToLearningCourseRoute,
} from "GlobalHeader/modules/Module";
import JumpToSection from "AppComponents/GlobalSearchBarV2/components/JumpToSection";
import classNames from "classnames";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import { getCurrentCurriculumContextForJumpTo } from "modules/CommonSelectors";
import {
  getVariablesForJumpToPermissions,
  getIsJumpToLargeOrgRevampEnabled,
} from "./JumpToModalUtils";
import withControlParameters from "lib/aclService/hoc/withControlParameters";
import { generateConfigurationQueryVariablesV2 } from "Utils/ConfigurationUtils/Utils";
import { WithConfigurationsAndPreferences } from "Utils/ConfigurationUtils/hoc";

const modalStyles = {
  header: {
    padding: "16px 16px 8px 24px",
  },
  body: {
    padding: 0,
  },
};

const closeModalTrigger = {
  OVERLAY_OR_ESCAPE: "OVERLAY_OR_ESCAPE",
  CLOSE_BUTTON: "CLOSE_BUTTON",
};

const TABS_LIMIT = 8;

const trackCurriculumChangeEvent = ({
  targetCurriculumId,
  platformCurriculumProgramId,
}) => {
  return recordEvent({
    eventName: "JUMP_TO",
    eventData: {
      event_type: "CURRICULUM_SWITCH",
      targetCurriculumId,
      platformCurriculumProgramId,
    },
  });
};

const CLOSE_SHORTCUT_TOOLTIP_PROPS = {
  align: { offset: [10, 0] },
  placement: "bottomRight",
  showArrow: false,
};

const getCurriculumTabOptions = ({ curriculumPrograms }) => {
  return _.map(curriculumPrograms, program => {
    return {
      value: program.id,
      label: program.acronym,
    };
  });
};

const getCurriculumTabOptionsMemoize = _.memoize(
  params => getCurriculumTabOptions(params),
  params => JSON.stringify(params)
);

const getCurriculumDropdownOptions = ({ curriculumPrograms }) => {
  return _.map(curriculumPrograms, program => {
    return {
      key: program.id,
      label: program.acronym,
    };
  });
};

const getCurriculumDropdownOptionsMemoize = _.memoize(
  params => getCurriculumDropdownOptions(params),
  params => JSON.stringify(params)
);

const INTERACTION_KEYS = [
  "ArrowUp",
  "ArrowDown",
  "ArrowRight",
  "ArrowLeft",
  "Enter",
  "Tab",
];

const JumpToModal = props => {
  const {
    onClose,
    updateJumpToCurriculumContext,
    t,
    curriculumPrograms,
    selectedCurriculum,
    navigateToFeature,
    goToCourseRoute,
    goToLearningCourseRoute,
    platformCurriculumProgramId,
    setActiveFeatureWithKeyboardShortcuts,
    shouldWaitForConfigurations,
    isLoading,
    isData,
  } = props;

  const availableShortcutsMapping = useRef({});
  const modalContainerRef = useRef();
  const tabsContainerRef = useRef();

  const curriculumProgramTabsOptions = getCurriculumTabOptionsMemoize({
    curriculumPrograms,
  });

  const renderCurriculumOptionsAsDropdown =
    _.size(curriculumPrograms) > TABS_LIMIT &&
    getIsJumpToLargeOrgRevampEnabled();

  const updateAvailableShortcutsMapping = ({ key, value }) => {
    availableShortcutsMapping.current[key] = value;
  };

  const getLoadingProps = () => {
    return shouldWaitForConfigurations ? { isLoading, isData } : {};
  };

  const onCurriculumChange = selectedProgram => {
    // reset available shortcuts mapping when user switches curriculum
    availableShortcutsMapping.current = {};
    updateJumpToCurriculumContext({
      selectedCurriculum: selectedProgram,
    });
  };

  const handleTabChange = curriculumId => {
    trackCurriculumChangeEvent({
      targetCurriculumId: curriculumId,
      platformCurriculumProgramId,
    });

    const selectedProgram = _.find(curriculumPrograms, { id: curriculumId });

    onCurriculumChange(selectedProgram);
  };

  useEffect(() => {
    let selectedCurriculum = _.find(curriculumPrograms, {
      id: platformCurriculumProgramId,
    });

    if (!selectedCurriculum) {
      selectedCurriculum = _.first(curriculumPrograms);
    }

    updateJumpToCurriculumContext({
      selectedCurriculum,
    });

    return () => {
      updateJumpToCurriculumContext({
        selectedCurriculum: null,
      });
    };
  }, []);

  useEffect(() => {
    const { focusableElements } = getFocusableElements();
    if (focusableElements) {
      setTimeout(() => {
        focusableElements[0]?.focus();
      });
    }
  }, [selectedCurriculum?.id]);

  const handleEnterKey = e => {
    e.stopPropagation();
    const currentFocusedElement = modalContainerRef.current?.querySelector(
      ":focus"
    );

    const testId = currentFocusedElement?.getAttribute("test-id");

    if (_.isEqual(testId, "seeMoreButton")) {
      const { focusableElements } = getFocusableElements();

      const focusedElementIndex = _.findIndex(
        focusableElements,
        currentFocusedElement
      );

      focusableElements[focusedElementIndex - 1]?.focus();
      currentFocusedElement?.click();
      return;
    }

    requestAnimationFrame(() => {
      onClose(closeModalTrigger.CLOSE_BUTTON);
      currentFocusedElement?.click();
    });
  };

  const closeJumpToFeature = () => {
    setActiveFeatureWithKeyboardShortcuts(null);
  };

  const handleTabKeyPress = e => {
    if (renderCurriculumOptionsAsDropdown) {
      const buttonDisclosure = document.getElementById(
        "curriculumDisclosureButton"
      );

      if (document.activeElement.id !== "curriculumDisclosureButton") {
        buttonDisclosure.focus();
        e.preventDefault();
        return;
      }

      return;
    }
    e.preventDefault();
    const container = modalContainerRef.current;
    if (!container) return;

    const selectedTabIndex = _.findIndex(curriculumProgramTabsOptions, {
      value: selectedCurriculum?.id,
    });

    const lastIndex = curriculumProgramTabsOptions.length - 1;

    let indexToFocusNext = selectedTabIndex;

    if (e.shiftKey) {
      // if user pressed shift + tab - we would move backward
      indexToFocusNext =
        selectedTabIndex === 0 ? lastIndex : selectedTabIndex - 1;
    } else {
      // if user pressed only tab - we would move forward
      indexToFocusNext =
        selectedTabIndex === lastIndex ? 0 : selectedTabIndex + 1;
    }

    if (
      indexToFocusNext >= 0 &&
      indexToFocusNext < curriculumProgramTabsOptions.length
    ) {
      // inputsArray[indexToFocusNext]?.click();
      // inputsArray[indexToFocusNext]?.focus();

      // HACK: Above method is not working when there is <Tabs> component
      // already present on the page from where this modal is triggered.
      handleTabChange(curriculumProgramTabsOptions?.[indexToFocusNext]?.value);
    }
  };

  const { areShortcutsEnabled } = useKeyboardShortcutsContext();

  const handleKeyDown = e => {
    const visibleJumpToFeatures = _.keys(availableShortcutsMapping.current);
    const flattenAvailableShortcutsMapping = _.reduce(
      visibleJumpToFeatures,
      (result, visibleJumpToFeature) => {
        return {
          ...result,
          ...availableShortcutsMapping.current[visibleJumpToFeature],
        };
      },
      {}
    );

    const matchingFeature = getFeatureFromKeyboardEvent({
      e,
      shortcutsMapping: flattenAvailableShortcutsMapping,
    });

    if (matchingFeature) {
      if (_.includes(matchingFeature, "LEARNING_COURSE")) {
        trackJumpToCardClick({
          e,
          featureType: JUMP_TO_COLUMNS.LEARNING_COURSE_FEED,
          featureId:
            flattenAvailableShortcutsMapping[matchingFeature].learningCourseId,
        });

        goToLearningCourseRoute({
          learningCourseId:
            flattenAvailableShortcutsMapping[matchingFeature].learningCourseId,
        });
      } else if (_.includes(matchingFeature, "COURSE")) {
        trackJumpToCardClick({
          e,
          featureType: JUMP_TO_COLUMNS.COURSE_FEED,
          featureId: flattenAvailableShortcutsMapping[matchingFeature].courseId,
        });

        goToCourseRoute({
          courseId: flattenAvailableShortcutsMapping[matchingFeature].courseId,
          learningCourseId:
            flattenAvailableShortcutsMapping[matchingFeature].learningCourseId,
        });
      } else if (_.includes(matchingFeature, "PROJECT")) {
        trackJumpToCardClick({
          e,
          featureType: JUMP_TO_COLUMNS.PROJECTS_FEED,
          featureId:
            flattenAvailableShortcutsMapping[matchingFeature].projectGroupType,
        });
        navigateToFeature({
          feature:
            flattenAvailableShortcutsMapping[matchingFeature].projectGroupType,
          allowCrossCurriculumNavigation: true,
          t,
        });
      } else {
        trackJumpToCardClick({
          e,
          featureType: JUMP_TO_COLUMNS.HOMEPAGE_FEATURES,
          featureId: matchingFeature,
        });
        navigateToFeature({
          feature: matchingFeature,
          allowCrossCurriculumNavigation: true,
          t,
        });
      }
      closeJumpToFeature();

      return true;
    }

    return false;
  };

  const getFocusableElements = () => {
    const allColumns = {};
    const focusableElements = [];

    let columnIndex = 0;
    let hasMoreColumns = true;

    /*  Calculate the total number of columns dynamically 
        and add them to the list of focusable elements */
    while (hasMoreColumns) {
      const columnElements = [
        ...(modalContainerRef.current?.querySelectorAll(
          `[data-elm=jump-to-col-${columnIndex}]`
        ) ?? []),
      ];

      if (columnElements.length > 0) {
        allColumns[columnIndex] = columnElements;
        focusableElements.push(...columnElements);
        columnIndex++;
      } else {
        hasMoreColumns = false;
      }
    }

    return { columnElements: allColumns, focusableElements };
  };

  const focusNextElement = (direction = "up") => e => {
    e.preventDefault();

    const { columnElements, focusableElements } = getFocusableElements();

    const currentFocusedElement = modalContainerRef.current?.querySelector(
      ":focus"
    );

    const currentFocusedElementIndex = _.findIndex(
      focusableElements,
      currentFocusedElement
    );

    if (currentFocusedElementIndex === -1 || currentFocusedElement === null) {
      focusableElements[0]?.focus();
      return;
    }

    let nextFocusElementIndex;
    const lastIndex = focusableElements.length - 1;

    const findElementInColumn = (columnIndex, rowIndex) => {
      const column = columnElements[columnIndex];
      if (!column || column.length === 0) return null;
      return column[rowIndex] ? column[rowIndex] : column[column.length - 1];
    };

    const columnKey = _.findKey(columnElements, elements =>
      _.includes(elements, currentFocusedElement)
    );

    if (direction === "up") {
      nextFocusElementIndex =
        currentFocusedElementIndex === 0
          ? lastIndex
          : currentFocusedElementIndex - 1;
    } else if (direction === "down") {
      nextFocusElementIndex =
        currentFocusedElementIndex === lastIndex
          ? 0
          : currentFocusedElementIndex + 1;
    } else if (direction === "left" || direction === "right") {
      const columnIdx = parseInt(columnKey, 10);
      const rowIdx = _.findIndex(
        columnElements[columnIdx],
        currentFocusedElement
      );

      const totalColumns = _.keys(columnElements).length;
      const maxColumnIndex = totalColumns - 1;

      let targetColumnIdx = columnIdx;

      /* Handle wrapping with dynamic column count */
      do {
        if (direction === "left") {
          targetColumnIdx =
            targetColumnIdx === 0 ? maxColumnIndex : targetColumnIdx - 1;
        } else if (direction === "right") {
          targetColumnIdx =
            targetColumnIdx === maxColumnIndex ? 0 : targetColumnIdx + 1;
        }
      } while (
        columnElements[targetColumnIdx]?.length === 0 &&
        targetColumnIdx !== columnIdx
      );

      if (targetColumnIdx !== columnIdx) {
        const targetElement = findElementInColumn(targetColumnIdx, rowIdx);
        if (targetElement) {
          targetElement.focus();
          setTimeout(() => targetElement.select?.(), 0);
          return;
        }
      }
      return;
    } else {
      console.error("invalid direction", direction);
      return;
    }

    focusableElements[nextFocusElementIndex]?.focus();
    setTimeout(() => {
      // select the texts in the element if the element is input element
      focusableElements[nextFocusElementIndex]?.select?.();
    }, 0);
  };

  useKey("ArrowUp", focusNextElement("up"), {}, []);
  useKey("ArrowDown", focusNextElement("down"), {}, []);
  useKey("ArrowLeft", focusNextElement("left"), {}, []);
  useKey("ArrowRight", focusNextElement("right"), {}, []);

  useKey("Enter", handleEnterKey, {}, []);
  useKey("Tab", handleTabKeyPress, {}, [selectedCurriculum?.id]);

  const handleShortcuts = pressedKeys => {
    const hasInvalidKey = _.some(
      pressedKeys,
      event => !event || _.includes(INTERACTION_KEYS, event.code)
    );

    if (hasInvalidKey) {
      return false;
    }

    return handleKeyDown(pressedKeys);
  };

  useKeyboardShortcuts({
    handleShortcuts,
    areShortcutsEnabled,
    maxShortcutLength: 2,
    areGlobalShortcuts: true,
  });

  const handleCurriculumDropdownChange = ({ key }) => {
    handleTabChange(key);
  };

  return (
    <Modal
      dsVersion="2.0"
      width="1200px"
      height="623px"
      isOpen={true}
      onClose={onClose(closeModalTrigger.OVERLAY_OR_ESCAPE)}
    >
      <Modal.Header style={modalStyles.header} isClosable={false}>
        <div className={classes.headerContainer}>
          <div className={classes.headerLeftContainer}>
            <span>{t("common:jump_to")}</span>
            {areShortcutsEnabled ? (
              <span
                className={classNames(
                  "text-body-s text-textSubtle",
                  classes.keyNameContainer
                )}
              >
                {"J"}
              </span>
            ) : null}
          </div>
          <div className={classes.headerRightContainer}>
            {renderCurriculumOptionsAsDropdown ? (
              <div>
                <Dropdown
                  overlay={
                    <DropdownMenu
                      dsVersion="2.0"
                      testId="jumpTo-jumpToModal-curriculumDropdown"
                      options={getCurriculumDropdownOptionsMemoize({
                        curriculumPrograms,
                      })}
                      size="medium"
                      onClick={handleCurriculumDropdownChange}
                      shouldStopPropagation
                    />
                  }
                >
                  <ButtonDisclosure
                    dsVersion="2.0"
                    variant={"neutral"}
                    type={"outlined"}
                    isFullWidth={false}
                    testId="jumpTo-jumpToModal-curriculumDropdownButtonTrigger"
                    id="curriculumDisclosureButton"
                    tabIndex={0}
                    width="200px"
                  >
                    <ConditionalTooltip
                      name={_.get(selectedCurriculum, "acronym")}
                      containerClass={classes.curriculumAcronym}
                      tooltipPlacement={"topRight"}
                    />
                  </ButtonDisclosure>
                </Dropdown>
              </div>
            ) : null}
            <ShortcutTooltip
              tooltipProps={CLOSE_SHORTCUT_TOOLTIP_PROPS}
              label={t("common:close")}
              shortcutKeys={["Esc"]}
            >
              <IconButton
                tabIndex={renderCurriculumOptionsAsDropdown ? 0 : "-1"}
                icon={<CloseOutlined />}
                dsVersion="2.0"
                variant="neutral"
                type="plain"
                size="medium"
                onClick={onClose(closeModalTrigger.CLOSE_BUTTON)}
                testId="jumpTo-jumpToModal-closeButton"
              />
            </ShortcutTooltip>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={modalStyles.body} isScrollable>
        <div className={classes.container} ref={modalContainerRef}>
          {!renderCurriculumOptionsAsDropdown ? (
            <div className={classes.tabsContainer} ref={tabsContainerRef}>
              <Tabs
                dsVersion="2.0"
                options={curriculumProgramTabsOptions}
                onChange={handleTabChange}
                value={_.get(selectedCurriculum, "id")}
                testId="jumpTo-jumpToModal-curriculumTabs"
                wrapperRef={tabsContainerRef}
              />
            </div>
          ) : null}
          {!_.isEmpty(selectedCurriculum) ? (
            <div className={classes.jumpToContainer}>
              <JumpToSection
                updateAvailableShortcutsMapping={
                  updateAvailableShortcutsMapping
                }
                {...getLoadingProps()}
              />
            </div>
          ) : null}
        </div>
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const {
    id: userId,
    userEntityType,
    org_id: orgId,
    userCurriculumPrograms: curriculumPrograms,
  } = userInfo;

  const curriculumProgramType = _.uniq(_.map(curriculumPrograms, "type"));

  const platformCurriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id"
  );

  const configurationQueryVariables = getConfigurationQueryVariable(state);

  const selectedCurriculum = getCurrentCurriculumContextForJumpTo(state);

  const shouldWaitForConfigurations = !_.isEqual(
    platformCurriculumProgramId,
    selectedCurriculum?.id
  );

  const userCurriculumPrograms = userInfo.userCurriculumPrograms;

  return {
    showErrorComp: false,
    orgId,
    userId,
    userEntityType,
    userInfo,
    configurationQueryVariables,
    curriculumProgramType,
    isData: true,
    isLoading: false,
    curriculumPrograms,
    selectedCurriculum,
    platformCurriculumProgramId,
    shouldWaitForConfigurations,
    userCurriculumPrograms,
  };
};

const mapActionCreators = {
  updateJumpToCurriculumContext,
  setActiveFeatureWithKeyboardShortcuts,
  navigateToFeature,
  goToCourseRoute,
  goToLearningCourseRoute,
};

export default compose(
  withRouter,
  I18nHOC({
    resource: [
      "common",
      "teacherHomePage",
      "classRoom",
      "configuration",
      "attendance",
      "integration",
    ],
  }),
  connect(mapStateToProps, mapActionCreators),
  withControlParameters({
    options: ({ userCurriculumPrograms }) => {
      return {
        variables: getVariablesForJumpToPermissions({
          curriculumPrograms: userCurriculumPrograms,
        }),
      };
    },
  }),
  getConfigurationQueryEnhancer(globalSearchConfigurationSelector, {
    fetchPolicy: "cache-and-network",
  }),
  WithConfigurationsAndPreferences({
    skip: ({ selectedCurriculum }) => _.isEmpty(selectedCurriculum),
    options: ({ selectedCurriculum }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        currentCurriculumProgramId: selectedCurriculum.id,
        src: "CURRICULUM_LEVEL",
        modules: ["TEACHER_HOMEPAGE"],
      }),
    }),
  })
)(JumpToModal);
